import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Grid, 
  TextField, Select, MenuItem, FormControl, InputLabel, Snackbar,
  Accordion, AccordionSummary, AccordionDetails, Chip, Divider,
  IconButton, Tooltip, Paper, List, ListItem, ListItemText,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete
} from '@mui/material';
import {
  EmailOutlined as EmailIcon, SendOutlined as SendIcon, PreviewOutlined as PreviewIcon,
  ExpandMore as ExpandMoreIcon, ContentCopyOutlined as CopyIcon,
  CheckCircleOutline as CheckIcon, ErrorOutline as ErrorIcon, VisibilityOutlined as VisibilityIcon,
  RefreshOutlined as RefreshIcon
} from '@mui/icons-material';
import api from '../api';

const EmailTemplateManager = () => {
  const [selectedTemplate, setSelectedTemplate] = useState('welcome');
  const [testEmail, setTestEmail] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState('data'); // 'data' or 'html'
  const [previewDialog, setPreviewDialog] = useState(false);
  
  // Real data from database
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [templateData, setTemplateData] = useState({});
  
  // Selected data for email
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);

  const templates = [
    {
      name: 'welcome',
      label: 'Welcome Email',
      description: 'Sent to new tenants when they move in',
      requiredData: ['propertyId', 'unitId', 'tenantId']
    },
    {
      name: 'rentReminder',
      label: 'Rent Reminder',
      description: 'Payment reminder for tenants',
      requiredData: ['propertyId', 'unitId', 'tenantId']
    },
    {
      name: 'maintenanceUpdate',
      label: 'Maintenance Update',
      description: 'Status updates for maintenance requests',
      requiredData: ['propertyId', 'unitId', 'tenantId'],
      optionalData: ['contractorId', 'maintenanceData']
    },
    {
      name: 'leaseRenewal',
      label: 'Lease Renewal',
      description: 'Lease renewal notifications',
      requiredData: ['propertyId', 'unitId', 'tenantId'],
      optionalData: ['leaseId', 'renewalData']
    }
  ];

  // Fetch properties
  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching properties',
        severity: 'error'
      });
    }
  };

  // Fetch units for a property
  const fetchUnits = async (propertyId) => {
    if (!propertyId) return;
    try {
      const response = await api.get(`/api/properties/${propertyId}/units`);
      setUnits(response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching units',
        severity: 'error'
      });
    }
  };

  // Fetch tenants for a unit
  const fetchTenants = async (propertyId, unitId) => {
    if (!propertyId || !unitId) return;
    try {
      const response = await api.get(`/api/properties/${propertyId}/units/${unitId}/tenants`);
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching tenants',
        severity: 'error'
      });
    }
  };

  // Fetch contractors
  const fetchContractors = async () => {
    try {
      const response = await api.get('/api/contractors');
      setContractors(response.data || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching contractors',
        severity: 'error'
      });
    }
  };

  // Fetch template data from backend
  const fetchTemplateData = async () => {
    if (!selectedProperty?.id || !selectedUnit?.id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/api/test-email/data/${selectedProperty.id}/${selectedUnit.id}`);
      setTemplateData(response.data.data);
    } catch (error) {
      console.error('Error fetching template data:', error);
      setSnackbar({
        open: true,
        message: 'Error fetching template data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchProperties();
    fetchContractors();
  }, []);

  // Update units when property changes
  useEffect(() => {
    if (selectedProperty) {
      fetchUnits(selectedProperty.id);
      setSelectedUnit(null);
      setSelectedTenant(null);
    }
  }, [selectedProperty]);

  // Update tenants when unit changes
  useEffect(() => {
    if (selectedProperty && selectedUnit) {
      fetchTenants(selectedProperty.id, selectedUnit.id);
      setSelectedTenant(null);
    }
  }, [selectedProperty, selectedUnit]);

  // Fetch template data when tenant is selected
  useEffect(() => {
    if (selectedProperty && selectedUnit && selectedTenant) {
      fetchTemplateData();
    }
  }, [selectedProperty, selectedUnit, selectedTenant]);

  // Generate HTML preview based on template and data
  const generateHtmlPreview = () => {
    const currentTemplate = templates.find(t => t.name === selectedTemplate);
    if (!currentTemplate) return '';

    const data = templateData;
    
    switch (selectedTemplate) {
      case 'welcome':
        return `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="margin: 0; font-size: 28px;">Welcome to ${data.property?.name || 'Your New Home'}!</h1>
              <p style="margin: 10px 0 0 0; font-size: 16px;">We're excited to have you as our newest tenant</p>
            </div>
            
            <div style="background: white; padding: 30px; border: 1px solid #e0e0e0; border-top: none;">
              <h2 style="color: #333; margin-top: 0;">Hello ${data.tenant?.name || 'there'}!</h2>
              
              <p style="color: #666; line-height: 1.6;">
                Welcome to <strong>${data.property?.name || 'your new home'}</strong>! We're thrilled to have you as part of our community.
              </p>
              
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #333; margin-top: 0;">Your Property Details:</h3>
                <ul style="color: #666; line-height: 1.8;">
                  <li><strong>Property:</strong> ${data.property?.name || 'N/A'}</li>
                  <li><strong>Address:</strong> ${data.property?.address || 'N/A'}</li>
                  <li><strong>Unit:</strong> ${data.unit?.name || 'N/A'}</li>
                  <li><strong>Lease Start:</strong> ${data.tenant?.leaseStartDate || 'N/A'}</li>
                  <li><strong>Monthly Rent:</strong> $${data.tenant?.rentAmount || 'N/A'}</li>
                </ul>
              </div>
              
              <div style="background: #e3f2fd; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #1976d2; margin-top: 0;">Next Steps:</h3>
                <ol style="color: #666; line-height: 1.8;">
                  <li>Complete your tenant portal registration</li>
                  <li>Set up your rent payment method</li>
                  <li>Review your lease agreement</li>
                  <li>Schedule your move-in inspection</li>
                </ol>
              </div>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${data.company?.portalUrl || '#'}" style="background: #4caf50; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;">
                  Access Your Portal
                </a>
              </div>
              
              <div style="border-top: 1px solid #e0e0e0; padding-top: 20px; margin-top: 30px;">
                <p style="color: #666; font-size: 14px;">
                  <strong>Need Help?</strong><br>
                  Email: ${data.company?.email || 'contact@example.com'}<br>
                  Phone: ${data.company?.phone || '(555) 123-4567'}
                </p>
                <p style="color: #999; font-size: 12px; margin-top: 20px;">
                  Best regards,<br>
                  The ${data.company?.name || 'Property Management'} Team
                </p>
              </div>
            </div>
          </div>
        `;
        
      case 'rentReminder':
        return `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="margin: 0; font-size: 28px;">Rent Payment Reminder</h1>
              <p style="margin: 10px 0 0 0; font-size: 16px;">Important: Your rent payment is due soon</p>
            </div>
            
            <div style="background: white; padding: 30px; border: 1px solid #e0e0e0; border-top: none;">
              <h2 style="color: #333; margin-top: 0;">Hello ${data.tenant?.name || 'there'}!</h2>
              
              <p style="color: #666; line-height: 1.6;">
                This is a friendly reminder that your rent payment for <strong>${data.property?.name || 'your property'}</strong> is due soon.
              </p>
              
              <div style="background: #fff3e0; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ff9800;">
                <h3 style="color: #e65100; margin-top: 0;">Payment Details:</h3>
                <ul style="color: #666; line-height: 1.8;">
                  <li><strong>Amount Due:</strong> $${data.tenant?.rentAmount || 'N/A'}</li>
                  <li><strong>Due Date:</strong> ${data.tenant?.rentDueDate || 'N/A'}</li>
                  <li><strong>Property:</strong> ${data.property?.name || 'N/A'}</li>
                  <li><strong>Unit:</strong> ${data.unit?.name || 'N/A'}</li>
                </ul>
              </div>
              
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #333; margin-top: 0;">Payment Options:</h3>
                <div style="margin: 15px 0;">
                  <strong>Online Portal:</strong>
                  <p style="color: #666; margin: 5px 0;">Pay securely through your tenant portal</p>
                  <a href="${data.company?.portalUrl || '#'}" style="color: #1976d2; text-decoration: none;">Access Portal →</a>
                </div>
                <div style="margin: 15px 0;">
                  <strong>Bank Transfer:</strong>
                  <p style="color: #666; margin: 5px 0;">Contact us for bank details</p>
                </div>
                <div style="margin: 15px 0;">
                  <strong>Mail Check:</strong>
                  <p style="color: #666; margin: 5px 0;">${data.company?.address || 'Contact us for mailing address'}</p>
                </div>
              </div>
              
              <div style="border-top: 1px solid #e0e0e0; padding-top: 20px; margin-top: 30px;">
                <p style="color: #666; font-size: 14px;">
                  <strong>Questions?</strong><br>
                  Email: ${data.company?.email || 'contact@example.com'}<br>
                  Phone: ${data.company?.phone || '(555) 123-4567'}
                </p>
                <p style="color: #999; font-size: 12px; margin-top: 20px;">
                  Best regards,<br>
                  The ${data.company?.name || 'Property Management'} Team
                </p>
              </div>
            </div>
          </div>
        `;
        
      case 'maintenanceUpdate':
        return `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="margin: 0; font-size: 28px;">Maintenance Update</h1>
              <p style="margin: 10px 0 0 0; font-size: 16px;">Your maintenance request has been updated</p>
            </div>
            
            <div style="background: white; padding: 30px; border: 1px solid #e0e0e0; border-top: none;">
              <h2 style="color: #333; margin-top: 0;">Hello ${data.tenant?.name || 'there'}!</h2>
              
              <p style="color: #666; line-height: 1.6;">
                We have an update regarding your maintenance request for <strong>${data.property?.name || 'your property'}</strong>.
              </p>
              
              <div style="background: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #4caf50;">
                <h3 style="color: #2e7d32; margin-top: 0;">Maintenance Details:</h3>
                <ul style="color: #666; line-height: 1.8;">
                  <li><strong>Type:</strong> General Maintenance</li>
                  <li><strong>Status:</strong> Scheduled</li>
                  <li><strong>Property:</strong> ${data.property?.name || 'N/A'}</li>
                  <li><strong>Unit:</strong> ${data.unit?.name || 'N/A'}</li>
                  ${data.contractor ? `<li><strong>Contractor:</strong> ${data.contractor.name}</li>` : ''}
                </ul>
              </div>
              
              <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #333; margin-top: 0;">What's Next:</h3>
                <ol style="color: #666; line-height: 1.8;">
                  <li>Our team will contact you to schedule the work</li>
                  <li>You'll receive notification when work begins</li>
                  <li>We'll update you on completion</li>
                </ol>
              </div>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${data.company?.portalUrl || '#'}" style="background: #2196f3; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;">
                  View Details in Portal
                </a>
              </div>
              
              <div style="border-top: 1px solid #e0e0e0; padding-top: 20px; margin-top: 30px;">
                <p style="color: #666; font-size: 14px;">
                  <strong>Questions?</strong><br>
                  Email: ${data.company?.email || 'contact@example.com'}<br>
                  Phone: ${data.company?.phone || '(555) 123-4567'}
                </p>
                <p style="color: #999; font-size: 12px; margin-top: 20px;">
                  Best regards,<br>
                  The ${data.company?.name || 'Property Management'} Team
                </p>
              </div>
            </div>
          </div>
        `;
        
      case 'leaseRenewal':
        return `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
            <div style="background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); color: #333; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
              <h1 style="margin: 0; font-size: 28px;">Lease Renewal Offer</h1>
              <p style="margin: 10px 0 0 0; font-size: 16px;">Your lease is coming up for renewal</p>
            </div>
            
            <div style="background: white; padding: 30px; border: 1px solid #e0e0e0; border-top: none;">
              <h2 style="color: #333; margin-top: 0;">Hello ${data.tenant?.name || 'there'}!</h2>
              
              <p style="color: #666; line-height: 1.6;">
                Your lease for <strong>${data.property?.name || 'your property'}</strong> is coming up for renewal. We'd love to have you stay with us!
              </p>
              
              <div style="background: #f3e5f5; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #9c27b0;">
                <h3 style="color: #7b1fa2; margin-top: 0;">Current Lease Details:</h3>
                <ul style="color: #666; line-height: 1.8;">
                  <li><strong>Property:</strong> ${data.property?.name || 'N/A'}</li>
                  <li><strong>Unit:</strong> ${data.unit?.name || 'N/A'}</li>
                  <li><strong>Current Rent:</strong> $${data.tenant?.rentAmount || 'N/A'}</li>
                  <li><strong>Lease End Date:</strong> ${data.tenant?.leaseEndDate || 'N/A'}</li>
                </ul>
              </div>
              
              <div style="background: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #2e7d32; margin-top: 0;">Renewal Options:</h3>
                <ul style="color: #666; line-height: 1.8;">
                  <li><strong>6-month renewal:</strong> $${data.tenant?.rentAmount || 'N/A'}/month</li>
                  <li><strong>12-month renewal:</strong> $${data.tenant?.rentAmount || 'N/A'}/month</li>
                  <li><strong>Month-to-month:</strong> $${data.tenant?.rentAmount || 'N/A'}/month</li>
                </ul>
              </div>
              
              <div style="text-align: center; margin: 30px 0;">
                <a href="${data.company?.portalUrl || '#'}" style="background: #9c27b0; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;">
                  Renew Your Lease
                </a>
              </div>
              
              <div style="border-top: 1px solid #e0e0e0; padding-top: 20px; margin-top: 30px;">
                <p style="color: #666; font-size: 14px;">
                  <strong>Questions?</strong><br>
                  Email: ${data.company?.email || 'contact@example.com'}<br>
                  Phone: ${data.company?.phone || '(555) 123-4567'}
                </p>
                <p style="color: #999; font-size: 12px; margin-top: 20px;">
                  Best regards,<br>
                  The ${data.company?.name || 'Property Management'} Team
                </p>
              </div>
            </div>
          </div>
        `;
        
      default:
        return '<p>Template preview not available</p>';
    }
  };

  const handleTemplateChange = (templateName) => {
    setSelectedTemplate(templateName);
  };

  const handleSendTest = async () => {
    if (!testEmail || !selectedProperty || !selectedUnit || !selectedTenant) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      
      const requestData = {
        to: testEmail,
        propertyId: selectedProperty.id,
        unitId: selectedUnit.id,
        tenantId: selectedTenant.id
      };

      // Add optional data based on template
      if (selectedTemplate === 'maintenanceUpdate' && selectedContractor) {
        requestData.contractorId = selectedContractor.id;
      }

      const response = await api.post(`/api/test-email/${selectedTemplate}`, requestData);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `${templates.find(t => t.name === selectedTemplate)?.label} sent successfully!`,
          severity: 'success'
        });
      } else {
        throw new Error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || 'Failed to send email',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTemplate = () => {
    const htmlContent = generateHtmlPreview();
    navigator.clipboard.writeText(htmlContent).then(() => {
      setSnackbar({
        open: true,
        message: 'HTML template copied to clipboard!',
        severity: 'success'
      });
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handlePreviewOpen = () => {
    setPreviewDialog(true);
  };

  const handlePreviewClose = () => {
    setPreviewDialog(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Template Manager
      </Typography>
      
      <Grid container spacing={3}>
        {/* Template Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Template
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Email Template</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  label="Email Template"
                >
                  {templates.map((template) => (
                    <MenuItem key={template.name} value={template.name}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary">
                {templates.find(t => t.name === selectedTemplate)?.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Selection */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select Data
              </Typography>
              
              <Grid container spacing={2}>
                {/* Property Selection */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={properties}
                    getOptionLabel={(option) => option.name || 'Unknown Property'}
                    value={selectedProperty}
                    onChange={(event, newValue) => setSelectedProperty(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Property"
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Unit Selection */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={units}
                    getOptionLabel={(option) => option.name || option.number || 'Unknown Unit'}
                    value={selectedUnit}
                    onChange={(event, newValue) => setSelectedUnit(newValue)}
                    disabled={!selectedProperty}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Unit"
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Tenant Selection */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={tenants}
                    getOptionLabel={(option) => option.name || 'Unknown Tenant'}
                    value={selectedTenant}
                    onChange={(event, newValue) => setSelectedTenant(newValue)}
                    disabled={!selectedUnit}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tenant"
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* Contractor Selection (for maintenance template) */}
                {selectedTemplate === 'maintenanceUpdate' && (
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      options={contractors}
                      getOptionLabel={(option) => option.name || 'Unknown Contractor'}
                      value={selectedContractor}
                      onChange={(event, newValue) => setSelectedContractor(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contractor (Optional)"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Test Email */}
                <Grid item xs={12}>
                  <TextField
                    label="Test Email Address"
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    fullWidth
                    required
                    placeholder="Enter email address to send test to"
                  />
                </Grid>
              </Grid>

              {/* Data Preview */}
              {Object.keys(templateData).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Data Preview
                  </Typography>
                  <Paper sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '12px' }}>
                      {JSON.stringify(templateData, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchTemplateData}
                  disabled={!selectedProperty || !selectedUnit || !selectedTenant}
                >
                  Refresh Data
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreviewOpen}
                  disabled={!selectedProperty || !selectedUnit || !selectedTenant}
                >
                  Preview Email
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyTemplate}
                  disabled={!selectedProperty || !selectedUnit || !selectedTenant}
                >
                  Copy HTML
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSendTest}
                  disabled={!testEmail || !selectedProperty || !selectedUnit || !selectedTenant || loading}
                >
                  {loading ? 'Sending...' : 'Send Test Email'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Email Preview
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant={previewMode === 'data' ? 'contained' : 'outlined'}
              onClick={() => setPreviewMode('data')}
            >
              Data
            </Button>
            <Button
              size="small"
              variant={previewMode === 'html' ? 'contained' : 'outlined'}
              onClick={() => setPreviewMode('html')}
            >
              HTML
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewMode === 'data' ? (
            <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '12px' }}>
                {JSON.stringify(templateData, null, 2)}
              </pre>
            </Paper>
          ) : (
            <Box
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1
              }}
              dangerouslySetInnerHTML={{ __html: generateHtmlPreview() }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: snackbar.severity === 'success' ? 'success.main' : 'error.main',
            color: 'white',
            p: 2,
            borderRadius: 1
          }}
        >
          {snackbar.severity === 'success' ? <CheckIcon /> : <ErrorIcon />}
          {snackbar.message}
        </Box>
      </Snackbar>
    </Box>
  );
};

export default EmailTemplateManager; 